import { Component, Input, ViewEncapsulation, Inject } from '@angular/core';
import { CurrencyPipe } from '@angular/common';
import { OvsComponent, Selectable, Core, QueryParamService } from '../../shared/core/ovs.core';
import { ResortDetail, ResortInventory } from '../../models/presentation.model';
import { ResortInventoryGroup } from '../../models/browser.model';
import { ResortComponent } from '../../shared/resort.component';
import { NgbDateStruct, NgbDatepicker } from '@ng-bootstrap/ng-bootstrap';
import { ResortInventoryClientBase } from '../../services/resort-inventory.client';
import { MatDialogRef, MAT_DIALOG_DATA, MatSliderChange } from '@angular/material';
import * as moment from 'moment';
import { UpdateClientService } from '../../services/update-client.service';
import { ResortDetailsPageEvents, CheckoutPageEvents } from '../../instrumentation/resort-custom-events';
import { UserActivityLogger, UserActivityLoggerOptions } from '../../instrumentation/user-activity-logger.service';

@Component({
    selector: 'room-availability',
    templateUrl: './room-availability.component.html',
    styleUrls: ['./room-availability.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class RoomAvailabilityComponent extends ResortComponent {
    // #region PRIVATE/PROTECTED MEMBERS
    private _inventoryClient: ResortInventoryClientBase;
    private _thisDialog: MatDialogRef<RoomAvailabilityComponent, ResortInventory>;
    private _core: Core;
    // #endregion

    // #region BINDABLE MEMBERS

    public group: ResortInventoryGroup;
    public selectedDate: NgbDateStruct;
    public nights: Selectable<number>[] = [];
    public sleeps: Selectable<number>[] = [];
    public availability: ResortInventory[] = [];
    public filteredResults: ResortInventory[] = [];
    public exactResults: ResortInventory[] = [];
    public allNearResults: ResortInventory[] = [];
    public nearResults: ResortInventory[] = [];
    public minPrice: number = 0;
    public maxPrice: number = 1;
    public selectedPrice: number = 0;
    public shouldSortByHighestCost: boolean = false;
    public shouldSortByLowestCost: boolean = false;
    public shouldSortByBestValue: boolean = true;

    private _previousDate: Date = null;
    //private _filetUpdated: boolean = false;

    // #endregion

    // #region PROPERTIES
    // #endregion

    // #region PUBLIC METHODS

    constructor(private updateClientService: UpdateClientService, inventoryClient: ResortInventoryClientBase
        , thisDialog: MatDialogRef<RoomAvailabilityComponent, ResortInventory>, @Inject(MAT_DIALOG_DATA) dialogData: ResortInventoryGroup
        , core: Core, private qryParamService: QueryParamService) {
        super();
        this._core = core;
        this.group = dialogData;
        this._thisDialog = thisDialog;
        this._inventoryClient = inventoryClient;
    }

    protected initialize(): void {
        super.initialize();
        let initialStartDate: Date = this.qryParamService.resortDetailParams.startDate;
        this.selectedDate = <NgbDateStruct>{ year: initialStartDate.getFullYear(), month: initialStartDate.getMonth() + 1, day: initialStartDate.getDate() };
    }

    public onSelectedPriceChanged(change: MatSliderChange): void {
        this.selectedPrice = change.value;
        UserActivityLogger.logEvent(ResortDetailsPageEvents.AvailabitlityModalResultsFilterApplied, { filter: "price" }, UserActivityLoggerOptions.AppInsightsOnly);
        this.onFilterChanged();
    }

    handleErr(e) {
        UserActivityLogger.logEvent(ResortDetailsPageEvents.ResortDetailFailure, this.mapAppInsightsCustomEvents(e.message, e.status), UserActivityLoggerOptions.AppInsightsOnly);
    }

    public onSelectedDateChanged(newDate: NgbDateStruct, calendar: NgbDatepicker): void {
        try {
            let date: Date = new Date(this.selectedDate.year, this.selectedDate.month - 1, this.selectedDate.day);
            if (this._previousDate != null && this._previousDate != date) {
                UserActivityLogger.logEvent(ResortDetailsPageEvents.AvailabilityModalDateFilterApplied
                    , { filter: "checkInDate" }, UserActivityLoggerOptions.AppInsightsOnly);
            }
            this._previousDate = date;
            var self = this;
            this._inventoryClient.Get(this.group.resort.id, date, r => this.onGetInventoryCompleted(r, calendar),self.handleErr.bind(this));
        } catch (ex) {
            console.log(ex);
        }

    }

    public getSelectedDate(): Date {
        return new Date(this.selectedDate.year, this.selectedDate.month - 1, this.selectedDate.day);
    }

    private onGetInventoryCompleted(inventory: ResortInventory[], calendar: NgbDatepicker): void {

        if (!inventory)
            UserActivityLogger.logEvent(ResortDetailsPageEvents.ResortDetailFailure, this.mapAppInsightsCustomEvents("Inventory Details Loading Failed", ""), UserActivityLoggerOptions.AppInsightsOnly);

        this.availability = inventory;

        if (this.configuration.ShowPricePerDay) {
            this.minPrice = inventory.min(i => i.dailyDiscountedPrice);
            this.maxPrice = inventory.max(i => i.dailyDiscountedPrice);
        }
        else {
            this.minPrice = inventory.min(i => i.discountedPrice);
            this.maxPrice = inventory.max(i => i.discountedPrice);
        }

        this.selectedPrice = this.maxPrice;

        let oldNights: Selectable<number>[] = this.nights.where(n => n.isSelected).toArray();
        let oldSleeps: Selectable<number>[] = this.sleeps.where(n => n.isSelected).toArray();

        this.nights = this.availability.select(a => a.nights).distinct().select(a => new Selectable<number>(a)).toArray();
        this.sleeps = this.availability.select(a => a.maxOccupancy).distinct().select(a => new Selectable<number>(a)).toArray();

        this.nights.forEach(v => {
            v.isSelected = oldNights.contains(v, (l, r) => l.value == r.value);
        });

        this.sleeps.forEach(v => {
            v.isSelected = oldSleeps.contains(v, (l, r) => l.value == r.value);
        });

        let startDate: Date = new Date(this.selectedDate.year, this.selectedDate.month - 1);
        startDate = moment(startDate).subtract(1, "month").toDate();
        calendar.navigateTo({ year: startDate.getFullYear(), month: startDate.getMonth() + 1 });

        this.onFilterChanged();
    }

    public formatNumber(value: number): string {
        let pipe: CurrencyPipe = new CurrencyPipe(Core.current.currentConfiguration.Locale);
        let result: string = pipe.transform(value, Core.current.currentConfiguration.CurrencyCode, "symbol", "1.2-2", Core.current.currentConfiguration.Locale);
        return result;
    }

    public calendarMovePrevious(calendar: NgbDatepicker): void {
        let startDate: Date = new Date(calendar.model.firstDate.year, calendar.model.firstDate.month - 1);
        startDate = moment(startDate).subtract(1, "month").toDate();
        calendar.navigateTo({ year: startDate.getFullYear(), month: startDate.getMonth() + 1 });
    }

    public calendarMoveNext(calendar: NgbDatepicker): void {
        let startDate: Date = new Date(calendar.model.firstDate.year, calendar.model.firstDate.month - 1);
        startDate = moment(startDate).add(1, "month").toDate();
        calendar.navigateTo({ year: startDate.getFullYear(), month: startDate.getMonth() + 1 });
    }

    public reset(): void {
        this.nights.forEach(n => n.isSelected = false);
        this.sleeps.forEach(n => n.isSelected = false);
        this.selectedPrice = this.maxPrice;
        this.onFilterChanged();
    }

    private sameDay(d1, d2) {
        return d1.getFullYear() === d2.getFullYear() &&
            d1.getMonth() === d2.getMonth() &&
            d1.getDate() === d2.getDate();
    }

    public onNightsChanged(): void {
        UserActivityLogger.logEvent(ResortDetailsPageEvents.AvailabitlityModalResultsFilterApplied, { filter: "nights" }, UserActivityLoggerOptions.AppInsightsOnly);
        this.onFilterChanged();
    }

    public onSleepsChanged(): void {
        UserActivityLogger.logEvent(ResortDetailsPageEvents.AvailabitlityModalResultsFilterApplied, { filter: "sleeps" }, UserActivityLoggerOptions.AppInsightsOnly);
        this.onFilterChanged();
    }

    private onFilterChanged(): void {
        let query: IQueryable<ResortInventory> = this.availability.asQueryable();
        let selectedNights: number[] = this.nights.where(n => n.isSelected).select(n => n.value).toArray();
        let selectedSleeps: number[] = this.sleeps.where(s => s.isSelected).select(n => n.value).toArray();

        if (this.configuration.ShowPricePerDay == true) {
            query = query.where(i => {
                return (selectedNights.length == 0 || selectedNights.contains(i.nights)) &&
                    (selectedSleeps.length == 0 || selectedSleeps.contains(i.maxOccupancy)) &&
                    i.dailyDiscountedPrice <= this.selectedPrice;
            });
        }
        else {
            query = query.where(i => {
                return (selectedNights.length == 0 || selectedNights.contains(i.nights)) &&
                    (selectedSleeps.length == 0 || selectedSleeps.contains(i.maxOccupancy)) &&
                    i.discountedPrice <= this.selectedPrice;
            });
        }

        if (this.shouldSortByBestValue)
            query = query.orderByDescending(i => i.percentSavings);
        else if (this.shouldSortByHighestCost)
            query = query.orderByDescending(i => i.discountedPrice);
        else if (this.shouldSortByLowestCost)
            query = query.orderBy(i => i.discountedPrice);

        this.filteredResults = query.toArray();
        let selectedDate: Date = this.getSelectedDate();
        this.allNearResults = [];
        this.nearResults = [];
        this.exactResults = [];
        this.allNearResults = this.filteredResults.where(i => selectedDate < i.checkInDate || selectedDate > i.checkOutDate).toArray();

        let idx: number = 0;
        for (idx = this.allNearResults.length - 1; idx >= 0; idx--) {
            let resortItem: ResortInventory = this.allNearResults[idx];
            let isSameDay: any = this.sameDay(selectedDate, resortItem.checkInDate);
            if (isSameDay == true) {
                this.allNearResults.splice(idx, 1);
                this.exactResults.add(resortItem);
            }
        }

        this.nearResults = [];
        this.showMoreAvailability();
    }

    public book(inventory: ResortInventory) {
        this.updateClientService.EnsureLatestVersion(() => {
            this._thisDialog.close(inventory);
        });
    }

    public showMoreAvailability(): void {
        let selectedDate: Date = this.getSelectedDate();
        let displayedCount: number = this.nearResults.length + 10;
        let totalCount: number = this.allNearResults.length;
        if (displayedCount > totalCount)
            displayedCount = totalCount;

        this.nearResults = this.allNearResults.orderBy(d => d.checkInDate).take(displayedCount).toArray();

        this.updateClientService.EnsureLatestVersion(() => { });
    }

    onScroll($event) {
        if ($event.currentTarget) {
            var scrollPercent = Math.round(100 * $event.currentTarget.scrollTop / $event.currentTarget.scrollHeight);
            if (scrollPercent > 50) this.showMoreAvailability();
        }
    }

    public close(): void {
        this._thisDialog.close();
    }

    public checkInDateClick(): void {

    }
    public closeAvailabilityModal() {
        UserActivityLogger.logEvent(ResortDetailsPageEvents.AvailabitlityModalCloseButtonClicked, {}, UserActivityLoggerOptions.AppInsightsOnly);
    }
    private mapAppInsightsCustomEvents(errorMessage, httpErrorCode) {
        const mapData = new Map();
        mapData.set("memberid", this.application.configuration.Member.Id);
        mapData.set("ovcpartnerid", this.application.configuration.Partner.Id);
        mapData.set("ovcpartner", this.application.configuration.Partner.LongName);
        mapData.set("memberclass", this.application.configuration.MemberClass);
        mapData.set("errormessage", errorMessage ? errorMessage : "Search error unknown");
        mapData.set("httperrorcode", httpErrorCode);
        mapData.set("product", "Resort");
        mapData.set("origination", "ICE");
        let jsonObject = {};
        mapData.forEach((value, key) => {
            jsonObject[key] = value;
        });
        return jsonObject;
    }

    // #endregion
}



// WEBPACK FOOTER //
// /home/vsts/work/1/s/Ovs.Resort.UI.Web/src/app/resort-detail/room-availability/room-availability.component.ts